import { INTEGRATION_PARTNERS, type PartnerComponents } from 'config/types';
import useAddressSheet from 'packages/shared/newOrder/hooks/useAddressSheet';
import AddressModuleWrapper from 'packages/shared/newOrder/modules/SelectService/components/AddressModuleWrapper';
import { selectIsShoeCareSelected } from 'pages/newOrder/newOrder.selectors';
import { setDefaultServiceLine } from 'pages/newOrder/newOrder.slice';
import type { FC } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectPartner } from 'redux-stores/slices/authSlice';
import { useShowEstimator } from 'store/cart-estimator';
import { lazyWithPreload, withComponentPreloading } from 'utils/bundle';
import { getPartnerSpecificComponent } from 'utils/helpers';

import type { PricingPageProps } from '../pricing.types';

const EstimatedBill = lazyWithPreload(() => import('../estimated-bill'));
const FooterWithBasket = lazyWithPreload(() => import('../footer-with-basket'));

const estimatedBillSheet: PartnerComponents<any> = {
  CAREEM: FooterWithBasket,
  Default: EstimatedBill,
};

const PartnerSpecificBottomSheet = getPartnerSpecificComponent(estimatedBillSheet);
const DELAY_SHEET_RENDER = 800; // ms

const AddressModule: FC<PricingPageProps> = ({ isBottomSheet, onClose }) => {
  const isShoeCareSelected = useSelector(selectIsShoeCareSelected);
  const [isSheetEnabled, setIsSheetEnabled] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const partnerId = useSelector(selectPartner);
  const showEstimator = useShowEstimator();
  const showEstimatorRef = useRef(showEstimator);
  const {
    continueToAddress,
    toggleAddressSheet,
    showAddressSheet,
    onClose: onCloseAddressSheet,
    onEdit: onEditAddress,
    onSubmit: onSubmitAddress,
    isLoading: isAddressSheetLoading,
  } = useAddressSheet({
    isSheetLoadingFeedback: true,
  });

  useEffect(() => {
    // Update the ref whenever showEstimator changes
    showEstimatorRef.current = showEstimator;
  }, [showEstimator]);

  const isCareemPartner = useMemo(() => partnerId === INTEGRATION_PARTNERS.CAREEM, [partnerId]);

  // Wokaround sheet mess with layout if transition is not completed
  useEffect(() => {
    setTimeout(() => {
      setIsSheetEnabled(true);
    }, DELAY_SHEET_RENDER);
  }, []);

  const onContinueOrder = useCallback(() => {
    if (isBottomSheet) {
      onClose?.();
    } else if (partnerId === INTEGRATION_PARTNERS.INSTASHOP) {
      // in case of instashop, open address sheet
      continueToAddress();
    } else {
      const isCareemAndEstimatorIsUsed = partnerId === INTEGRATION_PARTNERS.CAREEM && showEstimatorRef.current;

      if (isCareemAndEstimatorIsUsed || partnerId !== INTEGRATION_PARTNERS.CAREEM) {
        // in case of no serviceLine selected, set detault service line laundruy
        dispatch(setDefaultServiceLine());
      }

      // if estimator is used, navigate to flex step 1
      navigate(isCareemAndEstimatorIsUsed ? '/newOrder/flex?step=1' : '/newOrder/flex');
    }
  }, [dispatch, navigate, showEstimator, isBottomSheet, onClose]);

  const targetContainer = document.getElementById('bottomsheet-container-pricing-page')!!;

  const StickyEstimator = useMemo(() => {
    const Estimator = <PartnerSpecificBottomSheet isMoveToTopZIndex isSheetEnabled={isSheetEnabled} onContinue={onContinueOrder} />;
    return isBottomSheet && targetContainer ? ReactDOM.createPortal(Estimator, targetContainer) : Estimator;
  }, [isBottomSheet, onContinueOrder, isSheetEnabled, targetContainer]);

  return (
    <AddressModuleWrapper
      show={showAddressSheet}
      onSubmit={onSubmitAddress}
      toggleAddressSheet={toggleAddressSheet}
      onEdit={onEditAddress}
      onClose={onCloseAddressSheet}
      actionButtonProps={{
        isLoading: isAddressSheetLoading,
      }}
    >
      {(!isShoeCareSelected || isCareemPartner) && StickyEstimator}
    </AddressModuleWrapper>
  );
};

export default memo(
  withComponentPreloading<PricingPageProps>({
    components: [PartnerSpecificBottomSheet],
  })(AddressModule)
);
